/**
 * Model3DExpandedContent - 3D Model product expanded content
 *
 * Renders the expanded row content for 3D model products:
 * - Product image upload (left)
 * - 3D model upload + generate button (center)
 * - Variant links (right)
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box as BoxIcon, Eye, ImageIcon, Loader2, Wand2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useVendorPlan } from '../../../../../../common/hooks/use-vendor-plan.hook';
import { usePlanUsage } from '../../../../../../components/design-system/plan/usePlanUsage';
import { openUnlockDialog } from '../../../../../../components/design-system/plan/plan-limit-store';
import { isUnlimited, meterState } from '../../../../../../components/design-system/plan/plan-limits';
import type { Model3DExpandedContentProps } from './types';
import { Model3DPreviewDialog } from '../../shared/Model3DPreviewDialog';

/** Hover caption over an already-filled asset tile — used by both the image and the model tile. */
const ReplaceOverlay: React.FC<{ label: string }> = ({ label }) => (
  <span className="absolute inset-x-0 bottom-0 bg-foreground/60 py-1 text-center text-xs font-medium text-background opacity-0 transition-opacity group-hover:opacity-100">
    {label}
  </span>
);

export const Model3DExpandedContent: React.FC<Model3DExpandedContentProps> = ({
  variant,
  thumbnailUrl,
  onVariantImageChange,
  modelUrl,
  onVariantModelChange,
  getPendingModelFile,
  onGenerate3DModel,
  generating3DModels,
  usdzUrl,
  onVariantUsdzChange,
  onVariantLinkChange,
  onVariantAddToCartLinkChange,
}) => {
  const { t } = useTranslation();
  const { hasFeature } = useVendorPlan();
  const canGenerate3D = hasFeature('model3dGeneration');
  const isGenerating = generating3DModels.has(variant.uuid);
  /* The month's 3D jobs (Free: 1). Read only where the generate button shows. */
  const offersGenerate = !!thumbnailUrl && !modelUrl && !!onGenerate3DModel;
  const { meter } = usePlanUsage({ enabled: offersGenerate });
  const models = meter('max3dModelsPerMonth');
  const metered = !!models && !isUnlimited(models.limit);
  const spent = metered && meterState(models!.current, models!.limit) === 'at';
  const [previewOpen, setPreviewOpen] = React.useState(false);
  // Read when Preview is clicked: the file lives in a ref, so it isn't render state.
  const [previewFile, setPreviewFile] = React.useState<File | undefined>(undefined);

  return (
    <div className="flex items-start gap-6">
      {/* LEFT: Product Image */}
      <div className="flex-none">
        {thumbnailUrl ? (
          <label className="group relative block size-[140px] cursor-pointer overflow-hidden border border-border bg-muted/40 transition-colors hover:border-foreground/30">
            <img src={thumbnailUrl} alt="Product" className="size-full object-contain" />
            <ReplaceOverlay label={t('PAGES.NEW_PRODUCT.V2.VARIANT_TABLE.REPLACE')} />
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file && onVariantImageChange) {
                  onVariantImageChange(variant.uuid, file);
                }
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </label>
        ) : (
          <label className="flex size-[140px] cursor-pointer flex-col items-center justify-center gap-1.5 border border-dashed border-border text-muted-foreground transition-colors hover:border-foreground/30 hover:bg-muted/40">
            <ImageIcon className="size-6" strokeWidth={1.5} />
            <span className="text-center text-xs">
              {t('PAGES.NEW_PRODUCT.V3.MODEL3D.UPLOAD_IMAGE')}
            </span>
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file && onVariantImageChange) {
                  onVariantImageChange(variant.uuid, file);
                }
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </label>
        )}
      </div>

      {/* CENTER: 3D Model + iOS AR (USDZ) */}
      <div className="flex flex-none flex-col gap-2">
        {modelUrl ? (
          <label className="group relative flex size-[140px] cursor-pointer flex-col items-center justify-center gap-1.5 border border-success/30 bg-success/5 text-success transition-colors hover:border-success/50">
            <BoxIcon className="size-7" strokeWidth={1.5} />
            <span className="text-xs font-medium">{t('PAGES.NEW_PRODUCT.V3.MODEL3D.READY')}</span>
            <ReplaceOverlay label={t('PAGES.NEW_PRODUCT.V2.VARIANT_TABLE.REPLACE')} />
            <input
              type="file"
              hidden
              accept=".glb,.gltf"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file && onVariantModelChange) {
                  onVariantModelChange(variant.uuid, file);
                }
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </label>
        ) : (
          <div className="flex flex-col gap-2">
            <label
              className={cn(
                'flex w-[140px] cursor-pointer flex-col items-center justify-center gap-1.5 border border-dashed border-border text-muted-foreground transition-colors hover:border-foreground/30 hover:bg-muted/40',
                thumbnailUrl && onGenerate3DModel ? 'h-[100px]' : 'h-[140px]'
              )}
            >
              <BoxIcon className="size-6" strokeWidth={1.5} />
              <span className="text-center text-xs">{t('PAGES.NEW_PRODUCT.V3.MODEL3D.UPLOAD')}</span>
              <span className="text-center text-xs opacity-60">
                {t('PAGES.NEW_PRODUCT.V3.MODEL3D.FILE_TYPES')}
              </span>
              <input
                type="file"
                hidden
                accept=".glb,.gltf"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file && onVariantModelChange) {
                    onVariantModelChange(variant.uuid, file);
                  }
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </label>

            {/* Generate from image button */}
            {thumbnailUrl && !modelUrl && onGenerate3DModel && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (isGenerating) return;
                  // Not on the plan, or this month's used: say so and offer the way in, rather than a dead button.
                  if (!canGenerate3D || spent) {
                    openUnlockDialog({ quotaType: 'max3dModelsPerMonth', current: models?.current, limit: canGenerate3D ? models?.limit : 0 });
                    return;
                  }
                  onGenerate3DModel(variant.uuid);
                }}
                title={!canGenerate3D ? t('PLANS.LOCKED.FEATURE_UNAVAILABLE', 'Upgrade your plan to unlock 3D model generation.') : undefined}
                className={cn(
                  'flex w-[140px] items-center justify-center gap-1.5 border border-border bg-background py-1.5 text-xs font-medium text-muted-foreground transition-colors',
                  !canGenerate3D || spent
                    ? 'opacity-60 hover:opacity-100'
                    : isGenerating
                      ? 'cursor-default opacity-70'
                      : 'hover:border-foreground/30 hover:bg-muted'
                )}
              >
                {isGenerating ? <Loader2 className="size-3 animate-spin" /> : <Wand2 className="size-3" />}
                {isGenerating
                  ? t('PAGES.NEW_PRODUCT.V2.VARIANT_TABLE.GENERATING')
                  : t('PAGES.NEW_PRODUCT.V2.VARIANT_TABLE.GENERATE')}
              </button>
            )}
            {offersGenerate && metered && (
              <span
                data-testid="model3d-meter"
                className={cn('w-[140px] text-center text-[11px] tabular-nums', spent ? 'text-destructive' : 'text-muted-foreground')}
              >
                {t('FREE_PLAN.LIMITS.max3dModelsPerMonth')}: {t('FREE_PLAN.METER.OF', { current: models!.current, limit: models!.limit })}
              </span>
            )}
          </div>
        )}

        {/* Preview the (decrypted) 3D model in a dialog. */}
        {modelUrl && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setPreviewFile(getPendingModelFile?.(variant.uuid));
              setPreviewOpen(true);
            }}
            className="flex w-[140px] items-center justify-center gap-1.5 border border-border bg-background py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-foreground/30 hover:bg-muted"
          >
            <Eye className="size-3" />
            {t('PAGES.NEW_PRODUCT.V3.MODEL3D.PREVIEW', 'Preview 3D')}
          </button>
        )}
        <Model3DPreviewDialog
          open={previewOpen}
          productUuid={variant.uuid}
          localFile={previewFile}
          onClose={() => setPreviewOpen(false)}
        />

        {/* iOS AR Quick Look (USDZ) — optional secondary uploader.
            Android AR uses the primary GLTF above; iOS AR requires this USDZ file. */}
        <label
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'flex w-[140px] cursor-pointer items-center gap-1.5 border border-dashed px-2 py-1.5 transition-colors hover:bg-muted/40',
            usdzUrl
              ? 'border-success/40 bg-success/5 text-success hover:border-success/60'
              : 'border-border text-muted-foreground hover:border-foreground/30'
          )}
        >
          {/* Apple logo — no lucide equivalent; marks the iOS-specific asset. */}
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" className="shrink-0">
            <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" fill="currentColor" />
          </svg>
          <span className="flex-1 truncate text-xs font-medium">
            {usdzUrl
              ? t('PAGES.NEW_PRODUCT.V3.MODEL3D.USDZ_READY')
              : t('PAGES.NEW_PRODUCT.V3.MODEL3D.USDZ_UPLOAD')}
          </span>
          <input
            type="file"
            hidden
            accept=".usdz"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file && onVariantUsdzChange) {
                onVariantUsdzChange(variant.uuid, file);
              }
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </label>
      </div>

      {/* RIGHT: Variant Links */}
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <div className="space-y-1">
          <Label htmlFor={`variant-${variant.uuid}-detail-link`} className="text-xs font-medium text-muted-foreground">
            {t('PAGES.NEW_PRODUCT.V2.VARIANT_TABLE.DETAIL_LINK')}
          </Label>
          <Input
            id={`variant-${variant.uuid}-detail-link`}
            placeholder="https://..."
            value={variant.commerce.link || ''}
            onChange={(e) => onVariantLinkChange?.(variant.uuid, e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`variant-${variant.uuid}-cart-link`} className="text-xs font-medium text-muted-foreground">
            {t('PAGES.NEW_PRODUCT.V2.VARIANT_TABLE.ADD_TO_CART_LINK')}
          </Label>
          <Input
            id={`variant-${variant.uuid}-cart-link`}
            placeholder="https://..."
            value={variant.commerce.addToCartLink || ''}
            onChange={(e) => onVariantAddToCartLinkChange?.(variant.uuid, e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </div>
    </div>
  );
};

export default Model3DExpandedContent;
